import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import React, { useEffect, useMemo, useState } from "react";
import { BsUpcScan } from "react-icons/bs";
import { Search, X } from "lucide-react";
import { Scanner } from "@yudiel/react-qr-scanner";
import GoogleLinkRenderer from "../utils/GoogleLinkRenderer";
import AmazonLinkRenderer from "../utils/AmazonLinkRenderer";
import "./styles/modern-pages.css";
import "./styles/searchtool.css";

function SearchTool() {
  const [inputValue, setInputValue] = useState("");
  const [bolData, setBolData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchModel, setSearchModel] = useState("receivedItems");
  const [showScanner, setShowScanner] = useState(false);
  const [scannedValue, setScannedValue] = useState("");
  const [boundingBoxes, setBoundingBoxes] = useState([]);
  const [isScanMode, setIsScanMode] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const baseUrl = process.env.REACT_APP_DJANGO_API_BASE_URL;

  const columns = useMemo(
    () => [
      { headerName: "BOL", field: "BOL", enableValue: true, aggFunc: "count" },
      { headerName: "PkgID", field: "PkgID" },
      { headerName: "Google URL", field: "ItemDesc", cellRenderer: GoogleLinkRenderer },
      { headerName: "Amazon URL", field: "Asin", cellRenderer: AmazonLinkRenderer },
      { headerName: "Units", field: "Units", enableValue: true, aggFunc: "sum" },
      { headerName: "Unit Cost", field: "UnitCost" },
      { headerName: "Removal Reason", field: "RemovalReason" },
      { headerName: "Asin", field: "Asin" },
      { headerName: "FCSku", field: "FCSku" },
      { headerName: "Unit Recovery", field: "UnitRecovery" },
      { headerName: "Total Recovery Amount", field: "TotalRecovery" },
      { headerName: "Weight", field: "ItemPkgWeight" },
      { headerName: "Recovery Percentage %", field: "RecoveryRate" },
      { headerName: "Manifest Publish Date", field: "manifest_publish_date" },
      { headerName: "Item Description", field: "ItemDesc", minWidth: 200 },
      { headerName: "GL", field: "GL" },
      { headerName: "GL Description", field: "GLDesc" },
    ],
    []
  );

  const statusBar = useMemo(
    () => ({
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "left", key: "totalRowComponent" },
        { statusPanel: "agAggregationComponent", align: "right" },
      ],
    }),
    []
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleInputChange = (event) => {
    setInputValue(event.target.value.replace(/\s+/g, ""));
  };

  const handleSearch = (searchValue) => {
    const finalSearchValue = typeof searchValue === "string" ? searchValue : inputValue;
    if (!finalSearchValue.trim()) {
      setError("Enter a value to search.");
      return;
    }

    setIsLoading(true);
    setError(null);
    setScannedValue(`Searched for: ${finalSearchValue}`);

    fetch(`${baseUrl}/api/${searchModel}/?search=${encodeURIComponent(finalSearchValue)}`)
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => setBolData(data))
      .catch((requestError) => setError(`Search failed: ${requestError.message}`))
      .finally(() => setIsLoading(false));
  };

  const handleScan = (detectedCodes) => {
    if (!detectedCodes.length) return;
    const value = detectedCodes[0].rawValue;
    setScannedValue(`Scanned value: ${value}`);
    handleSearch(value);
    setTimeout(() => setShowScanner(false), 500);
  };

  const customTracker = (detectedCodes) => {
    setBoundingBoxes(detectedCodes.map((code) => code.boundingBox));
  };

  const resultCount = Array.isArray(bolData) ? bolData.length : 0;

  return (
    <div className="modern-page searchtool-container">
      <div className="modern-page-shell">
        <header className="modern-page-header searchtool-header">
          <h1 className="modern-page-title">Search Tool</h1>

          <div className="searchtool-header-controls">
            <div className="model-select" role="radiogroup" aria-label="Inventory source">
              {[
                ["manifests", "Manifest"],
                ["receivedItems", "Received"],
                ["missingItems", "Missing"],
              ].map(([value, label]) => (
                <label
                  className={`model-option${searchModel === value ? " selected" : ""}`}
                  key={value}
                >
                  <input
                    type="radio"
                    name="search-model"
                    value={value}
                    checked={searchModel === value}
                    onChange={(event) => setSearchModel(event.target.value)}
                  />
                  {label}
                </label>
              ))}
            </div>

            {isMobile && (
              <label className="scan-mode-toggle">
                <input
                  type="checkbox"
                  checked={isScanMode}
                  onChange={() => {
                    setIsScanMode((enabled) => !enabled);
                    setShowScanner(false);
                  }}
                />
                <span aria-hidden="true"></span>
                Scan
              </label>
            )}

            <div className="search-area">
              {isMobile && isScanMode ? (
                <button
                  type="button"
                  onClick={() => setShowScanner(true)}
                  className="modern-primary-button scanner-btn"
                >
                  <BsUpcScan /> Scan code
                </button>
              ) : (
                <>
                  <div className="search-input-wrap">
                    <Search size={17} aria-hidden="true" />
                    <input
                      type="text"
                      value={inputValue}
                      onChange={handleInputChange}
                      placeholder="Enter BOL, package ID, ASIN or FCSku"
                      className="search-input"
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleSearch();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSearch()}
                    disabled={isLoading}
                    className="modern-primary-button search-btn"
                  >
                    {isLoading ? (
                      <span className="modern-dot-loader" aria-label="Searching">
                        <span></span><span></span><span></span>
                      </span>
                    ) : (
                      "Search"
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </header>

        {showScanner && (
          <section className="modern-panel scanner-panel">
            <div className="modern-panel-header">
              <h2>Scan code</h2>
              <button
                type="button"
                className="scanner-close"
                onClick={() => setShowScanner(false)}
                aria-label="Close scanner"
              >
                <X size={18} />
              </button>
            </div>
            <div className="scanner-container">
              <Scanner
                onScan={handleScan}
                formats={["code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "data_matrix", "matrix_codes"]}
                onError={(scannerError) => setError(`Scanner error: ${scannerError.message}`)}
                scanDelay={300}
                components={{ tracker: customTracker }}
                paused={false}
                styles={{ width: "100%" }}
              />
              {boundingBoxes.map((box, index) => (
                <span
                  key={index}
                  className="scanner-tracker"
                  style={{ left: box.x, top: box.y, width: box.width, height: box.height }}
                />
              ))}
            </div>
          </section>
        )}

        {(error || scannedValue) && (
          <div className={`search-feedback${error ? " error" : ""}`}>
            {error || scannedValue}
          </div>
        )}

        <section className="modern-panel search-results-panel">
          <div className="modern-panel-header">
            <h2>Results</h2>
            {bolData && <span className="modern-page-meta">{resultCount} items</span>}
          </div>
          {isLoading ? (
            <div className="modern-empty-state">
              <span className="modern-dot-loader" aria-label="Loading results">
                <span></span><span></span><span></span>
              </span>
            </div>
          ) : bolData ? (
            <div className="ag-grid-area ag-theme-quartz">
              <AgGridReact
                columnDefs={columns}
                rowData={bolData}
                domLayout="autoHeight"
                enableRangeSelection
                statusBar={statusBar}
              />
            </div>
          ) : (
            <div className="modern-empty-state">
              <Search size={25} aria-hidden="true" />
              <span>Search inventory to view results.</span>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default SearchTool;
